import { Box, desktopFirstMediaQuery, Icon, Text, Title, useTheme } from '@inflearn/ds-react';
import { css } from '@emotion/react';
import { unescape } from '../../../../utils/escape';
import { getEllipsisStyle } from '../../../../utils/getEllipsisStyle';
import { type TitleOrder } from '@mantine/core';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';

export type InfoCardType = {
  title: string;
  description: string;
  imageSrc: string;
  href: string;
  titleLevel?: TitleOrder;
};

type InfoCardProps = InfoCardType;
const EventCard = ({ title, description, imageSrc, href, titleLevel = 3 }: InfoCardProps) => {
  const theme = useTheme();
  return (
    <a
      href={href}
      target={'_blank'}
      rel="noreferrer">
      <Box
        component={'article'}
        css={[styleCard, {
          transition: 'transform .25s ease-in-out',
          '&:hover': {
            transform: 'translateY(-6px)',
            svg: {
              opacity: 0.5
            },
            img: {
              filter: 'brightness(0.8)'
            }
          }
        }]}>
        <Box component={'figure'} css={styleCardImageWrapper}>
          <img alt={`${title}의 썸네일`} src={imageSrc} css={styleCardImage} />
          <Icon
            icon={faArrowUpRightFromSquare}
            size={40}
            css={{ position: 'absolute', opacity: 0, transition: 'opacity ease-in-out 0.25s' }}
            color="white"
          />
        </Box>
        <Box css={styleTextWrapper}>
          <Title
            size={20}
            color={theme.colors.dark[9]}
            weight={'bold'}
            order={titleLevel}
            css={[styleTitle, getEllipsisStyle(2)]}>
            {unescape(title)}
          </Title>
          <Text
            size={16}
            color={theme.colors.gray[5]}
            css={[styleDescription, getEllipsisStyle(2)]}>
            {unescape(description)}
          </Text>
        </Box>
      </Box>
    </a>
  );
};

export const styleCard = css({
  display: 'flex',
  flexFlow: 'column nowrap',
  justifyContent: 'flex-start',
  alignItems: 'start',

  width: '100%',
  height: '100%',
  borderRadius: '16px',
  margin: 0,
  padding: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
  boxShadow:
    '0px 0.7px 0.7px -0.4px rgba(0, 0, 0, 0.02), 0px 2px 2px -0.8px rgba(0, 0, 0, 0.02081), 0px 4.4px 4.4px -1.2px rgba(0, 0, 0, 0.02177), 0px 9.8px 9.8px -1.6px rgba(0, 0, 0, 0.02), 0px 25px 25px -2px rgba(0, 0, 0, 0.03)',
  overflow: 'hidden',
  transition: 'transform .25s ease-in-out',

  ':hover': {
    transform: 'translateY(-6px)'
  }
});

export const styleCardImageWrapper = css({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: '0 0 200px',
  overflow: 'hidden',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    flex: '0 0 auto'
  }
});

export const styleCardImage = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  [desktopFirstMediaQuery.mobile]: {
    width: '100%',
    height: 'auto',
    position: 'relative'
  }
});

export const styleTextWrapper = css({
  flex: '1 1 100%',
  display: 'grid',
  gridTemplateRows: '1fr auto',
  rowGap: '12px',

  width: '100%',
  padding: '24px'
});

const styleTitle = css({
  maxHeight: '52px'
});

const styleDescription = css({});

export default EventCard;
